import React, { useState, useContext, useMemo } from 'react';
import PropTypes from 'prop-types';
import tabStyle from './tabs.module.scss';

const Tab = ({
  onClick, children, isSelected, tabLength,
}) => (
  <div
    aria-hidden
    className={`${tabStyle['tab-list-item']} ${isSelected && tabStyle.selected}`}
    style={{ width: `${(1 / tabLength) * 100}%` }}
    onClick={onClick}
  >
    <span>
      <span>{children}</span>
      <span className={tabStyle['tab-selected-line']} />
    </span>
  </div>
);

Tab.propTypes = {
  onClick: PropTypes.func,
  children: PropTypes.string.isRequired,
  isSelected: PropTypes.bool,
  tabLength: PropTypes.number,
};

Tab.defaultProps = {
  onClick: () => {},
  isSelected: false,
  tabLength: 0,
};

const TabsContext = React.createContext({});

const TabPanels = ({ children }) => {
  const context = useContext(TabsContext);
  const { width } = context;
  return (
    <div style={{ width: `${width}` }}>
      {React.Children.map(children, (child, index) => React.cloneElement(child, {
        isSelected: index === context.selectedIndex,
      }))}
    </div>
  );
};

TabPanels.propTypes = {
  children: PropTypes.arrayOf(PropTypes.element).isRequired,
};


const TabPanel = ({ children, isSelected }) => {
  if (!isSelected) return null;
  return <div className="tab-panel-item">{children}</div>;
};

TabPanel.propTypes = {
  children: PropTypes.element.isRequired,
  isSelected: PropTypes.bool,
};

TabPanel.defaultProps = {
  isSelected: false,
};

const TabList = ({ children }) => {
  const context = useContext(TabsContext);
  const { width } = context;
  return (
    <div className={tabStyle['tab-list']} style={{ width: `${width}` }}>
      {React.Children.map(children, (child, index) => React.cloneElement(child, {
        onClick: () => context.setSelectedIndex(index),
        isSelected: index === context.selectedIndex,
        tabLength: children.length,
      }))}
    </div>
  );
};

TabList.propTypes = {
  children: PropTypes.arrayOf(PropTypes.element).isRequired,
};

const Tabs = ({
  children, defaultIndex, width, onChange,
}) => {
  const [selectedIndex, setSelectedIndex] = useState(
    typeof defaultIndex === 'number' ? defaultIndex : 0,
  );
  const context = useMemo(() => ({ selectedIndex, setSelectedIndex }), [
    selectedIndex,
    setSelectedIndex,
  ]);

  return (
    <TabsContext.Provider value={{ ...context, width, onChange }}>{children}</TabsContext.Provider>
  );
};

Tabs.propTypes = {
  children: PropTypes.arrayOf(PropTypes.element).isRequired,
  onChange: PropTypes.func,
  defaultIndex: PropTypes.number,
  width: PropTypes.string,
};

Tabs.defaultProps = {
  defaultIndex: 0,
  width: '300px',
  onChange: () => {},
};

Tabs.TabPanels = TabPanels;
Tabs.TabPanel = TabPanel;
Tabs.Tab = Tab;
Tabs.TabList = TabList;


export default Tabs;
